import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Category, HotKey, MessageType } from '../types';
import apiService from '../services/api';

const ROTATION_TAKEOVER = 'takeover';
const ROTATION_OVERRIDE = 'override';
const ROTATION_ROTATE_WITH = 'rotate_with';

const DURATION_OPTIONS = [15, 30, 60, 90, 120];

const BLACK = '#000000';
const DIALOG_BG = '#ebebeb';

/** #22: tighter controls to align with dashboard filter density */
const COMPACT_INPUT_PROPS = { sx: { fontSize: '0.8125rem', py: 0.5 } } as const;

const inputLabelSx = {
  color: BLACK,
  fontSize: '0.8125rem',
  '&.Mui-focused': { color: `${BLACK} !important` },
  '&.MuiInputLabel-shrink': { color: BLACK },
};

const textFieldOutlinedSx = {
  '& .MuiOutlinedInput-root': {
    color: BLACK,
    fontSize: '0.8125rem',
    '& fieldset': { borderColor: BLACK },
    '&:hover fieldset': { borderColor: BLACK },
    '&.Mui-focused fieldset': {
      borderColor: BLACK,
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-input': {
    py: 0.75,
    fontSize: '0.8125rem',
  },
  '& .MuiOutlinedInput-input::placeholder': {
    color: BLACK,
    opacity: 1,
  },
  '& .MuiFormHelperText-root': {
    color: BLACK,
    fontSize: '0.7rem',
    mt: 0.5,
  },
};

const selectOutlinedSx = {
  color: BLACK,
  fontSize: '0.8125rem',
  '& .MuiOutlinedInput-input': { py: 0.65, fontSize: '0.8125rem' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: BLACK },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BLACK },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: BLACK,
    borderWidth: '1px',
  },
  '& .MuiSvgIcon-root': { color: BLACK, fontSize: '1.1rem' },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      backgroundColor: '#ffffff',
      color: BLACK,
      '& .MuiMenuItem-root': { color: BLACK },
    },
  },
};

/** Quu API: 0 = takeover, 1 = rotate, 2 = override (#30). */
function rotationToApiType(rotation: string): string {
  if (rotation === ROTATION_TAKEOVER) return '0';
  if (rotation === ROTATION_ROTATE_WITH) return '1';
  if (rotation === ROTATION_OVERRIDE) return '2';
  return '1';
}

/** option_ids for API: 1 Music, 2 Other, 3 Ads */
function buildDisplayDuringOptions(music: boolean, ads: boolean, other: boolean): string[] {
  const o: string[] = [];
  if (music) o.push('1');
  if (other) o.push('2');
  if (ads) o.push('3');
  return o;
}

function isHttpImageUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export interface HotKeyFormValues {
  name: string;
  categoryId: string;
  categoryName: string;
  rotation: string;
  keepAliveMinutes: number;
  durationSeconds: number;
  duringMusic: boolean;
  duringAds: boolean;
  duringOther: boolean;
  line1: string;
  line2: string;
}

const defaultFormValues: HotKeyFormValues = {
  name: '',
  categoryId: '',
  categoryName: '',
  rotation: ROTATION_ROTATE_WITH,
  keepAliveMinutes: 90,
  durationSeconds: 30,
  duringMusic: false,
  duringAds: false,
  duringOther: true,
  line1: '',
  line2: '',
};

interface HotKeyFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialHotKey?: HotKey | null;
  categories: Category[];
}

const HotKeyFormModal: React.FC<HotKeyFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialHotKey,
  categories,
}) => {
  const [form, setForm] = useState<HotKeyFormValues>(defaultFormValues);
  const [saveAction, setSaveAction] = useState<'idle' | 'save' | 'publish'>('idle');
  const [error, setError] = useState('');
  const saving = saveAction !== 'idle';
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [persistedImageUrl, setPersistedImageUrl] = useState('');
  const [blobPreviewUrl, setBlobPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** Same save API as create; non-null id means update existing blast. */
  const editId =
    initialHotKey?.id != null && String(initialHotKey.id).trim() !== ''
      ? String(initialHotKey.id).trim()
      : null;
  const isEdit = editId != null;

  const revokeBlobIfNeeded = (url: string | null) => {
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!open) return;
    setError('');
    setImageFile(null);
    setBlobPreviewUrl((prev) => {
      revokeBlobIfNeeded(prev);
      return null;
    });
    setPersistedImageUrl(initialHotKey?.messageImage?.trim() ?? '');
    if (initialHotKey) {
      const line1 = (initialHotKey.rt1 ?? '').trim() || (initialHotKey.messageText || '').split(/\s*-\s*/)[0]?.trim() || '';
      const line2 = (initialHotKey.rt2 ?? '').trim() || (initialHotKey.messageText || '').split(/\s*-\s*/).slice(1).join(' - ').trim() || '';
      const catByKey = categories.find(
        (c) =>
          c.id === initialHotKey.categoryId ||
          c.name === initialHotKey.categoryId ||
          c.name === initialHotKey.category?.name
      );
      setForm({
        name: initialHotKey.name ?? '',
        categoryId: catByKey?.id ?? initialHotKey.category?.id ?? initialHotKey.categoryId ?? '',
        categoryName: catByKey?.name ?? initialHotKey.category?.name ?? initialHotKey.categoryId ?? '',
        rotation:
          initialHotKey.type === MessageType.TAKEOVER
            ? ROTATION_TAKEOVER
            : initialHotKey.type === MessageType.OVERRIDE
              ? ROTATION_OVERRIDE
              : ROTATION_ROTATE_WITH,
        keepAliveMinutes: initialHotKey.keepAliveMinutes ?? 90,
        durationSeconds: (() => {
          const d = initialHotKey.rotationDurationSeconds ?? 30;
          if (DURATION_OPTIONS.includes(d)) return d;
          return DURATION_OPTIONS.reduce((best, cur) =>
            Math.abs(cur - d) < Math.abs(best - d) ? cur : best
          );
        })(),
        duringMusic:
          initialHotKey.type === MessageType.TAKEOVER
            ? true
            : (initialHotKey.duringSettings?.music ?? false),
        duringAds:
          initialHotKey.type === MessageType.TAKEOVER
            ? true
            : (initialHotKey.duringSettings?.ads ?? false),
        duringOther:
          initialHotKey.type === MessageType.TAKEOVER
            ? true
            : (initialHotKey.duringSettings?.other ?? true),
        line1,
        line2,
      });
    } else {
      setForm({ ...defaultFormValues });
    }
  }, [open, initialHotKey, categories]);

  useEffect(() => {
    if (!open) {
      setImageFile(null);
      setBlobPreviewUrl((prev) => {
        revokeBlobIfNeeded(prev);
        return null;
      });
    }
  }, [open]);

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (e.g. PNG or JPEG).');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError(`Image must be ${MAX_IMAGE_BYTES / (1024 * 1024)} MB or smaller.`);
      return;
    }
    setError('');
    setImageFile(file);
    setBlobPreviewUrl((prev) => {
      revokeBlobIfNeeded(prev);
      return URL.createObjectURL(file);
    });
  };

  const clearImage = () => {
    setImageFile(null);
    setPersistedImageUrl('');
    setBlobPreviewUrl((prev) => {
      revokeBlobIfNeeded(prev);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const previewSrc = blobPreviewUrl || persistedImageUrl || undefined;

  const handleCategoryChange = (categoryId: string) => {
    const cat =
      categories.find((c) => c.id === categoryId) || categories.find((c) => c.name === categoryId);
    setForm((prev) => ({
      ...prev,
      categoryId,
      categoryName: cat?.name ?? categoryId,
    }));
  };

  const handleSubmit = async (activateFlag: 0 | 1) => {
    setError('');
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!form.categoryId && !form.categoryName) {
      setError('Category is required.');
      return;
    }
    const categoryName = form.categoryName || form.categoryId || categories[0]?.name || 'Misc';

    const takeover = form.rotation === ROTATION_TAKEOVER;
    const optMusic = takeover ? true : form.duringMusic;
    const optAds = takeover ? true : form.duringAds;
    const optOther = takeover ? true : form.duringOther;

    // BlastHotQuus/Save expects binary ImageFile. Prefer a newly chosen File;
    // if editing without a new file, pass the existing public URL so the API
    // layer can fetch and attach it as ImageFile when needed.
    const existingImageUrl = (() => {
      const existing = persistedImageUrl.trim();
      if (!existing || existing.startsWith('data:')) return null;
      if (isHttpImageUrl(existing)) return existing;
      return existing;
    })();

    setSaveAction(activateFlag === 1 ? 'publish' : 'save');
    try {
      await apiService.saveHotKey(
        {
          id: editId,
          name: form.name.trim(),
          category: categoryName,
          categoryId: form.categoryId,
          minutesAlive: Math.max(0, Math.floor(form.keepAliveMinutes)) || 90,
          rt1: form.line1.trim(),
          rt2: form.line2.trim(),
          imageFile: imageFile || null,
          imageUrl: imageFile ? null : existingImageUrl,
          radio_station_ids: [],
          type: rotationToApiType(form.rotation),
          durationInSeconds: form.durationSeconds,
          options: buildDisplayDuringOptions(optMusic, optAds, optOther),
        },
        activateFlag
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      if (err?.message === 'Session expired.' || err?.status === 401 || err?.response?.status === 401) {
        return;
      }
      setError(err.message || 'Failed to save.');
    } finally {
      setSaveAction('idle');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { backgroundColor: DIALOG_BG },
      }}
    >
      <DialogTitle
        sx={{
          color: BLACK,
          fontSize: '1rem',
          fontWeight: 600,
          py: 1.5,
          px: 2,
        }}
      >
        {isEdit ? 'Edit Message' : 'Create HotQuu'}
      </DialogTitle>
      <DialogContent sx={{ px: 2, pt: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.25 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 0.5 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            size="small"
            InputProps={COMPACT_INPUT_PROPS}
            InputLabelProps={{ sx: inputLabelSx }}
            sx={textFieldOutlinedSx}
          />

          <TextField
            label="Quu Line 1"
            value={form.line1}
            onChange={(e) => setForm((prev) => ({ ...prev, line1: e.target.value }))}
            fullWidth
            size="small"
            InputProps={COMPACT_INPUT_PROPS}
            InputLabelProps={{ sx: inputLabelSx }}
            sx={textFieldOutlinedSx}
          />
          <TextField
            label="Quu Line 2"
            value={form.line2}
            onChange={(e) => setForm((prev) => ({ ...prev, line2: e.target.value }))}
            fullWidth
            size="small"
            InputProps={COMPACT_INPUT_PROPS}
            InputLabelProps={{ sx: inputLabelSx }}
            sx={textFieldOutlinedSx}
          />

          <Box>
            <Typography sx={{ color: BLACK, mb: 0.5, fontSize: '0.8125rem', fontWeight: 600 }}>
              Image
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageFileChange}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                sx={{
                  color: BLACK,
                  borderColor: BLACK,
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  py: 0.25,
                  '&:hover': { borderColor: BLACK, backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                {imageFile ? 'Replace image' : 'Upload image'}
              </Button>
              {previewSrc && (
                <Button
                  type="button"
                  size="small"
                  onClick={clearImage}
                  disabled={saving}
                  sx={{ color: BLACK, textTransform: 'none', fontSize: '0.8125rem', py: 0.25 }}
                >
                  Remove
                </Button>
              )}
            </Box>
            {imageFile && (
              <Typography variant="caption" sx={{ display: 'block', color: BLACK, mt: 0.5 }}>
                {imageFile.name}
              </Typography>
            )}
            {previewSrc && (
              <Box
                component="img"
                src={previewSrc}
                alt=""
                sx={{
                  display: 'block',
                  mt: 1,
                  maxWidth: '100%',
                  maxHeight: 120,
                  objectFit: 'contain',
                  border: `1px solid ${BLACK}`,
                  borderRadius: 1,
                  backgroundColor: '#fff',
                }}
              />
            )}
          </Box>

          <FormControl fullWidth required size="small">
            <InputLabel sx={inputLabelSx}>Category</InputLabel>
            <Select
              value={form.categoryId || ''}
              label="Category"
              size="small"
              onChange={(e) => handleCategoryChange(e.target.value as string)}
              sx={selectOutlinedSx}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="" disabled>
                Select category
              </MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel sx={inputLabelSx}>Rotation</InputLabel>
            <Select
              value={form.rotation}
              label="Rotation"
              size="small"
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  rotation: v,
                  ...(v === ROTATION_TAKEOVER
                    ? { duringMusic: true, duringAds: true, duringOther: true }
                    : {}),
                }));
              }}
              sx={selectOutlinedSx}
              MenuProps={selectMenuProps}
            >
              <MenuItem value={ROTATION_TAKEOVER}>Takeover</MenuItem>
              <MenuItem value={ROTATION_OVERRIDE}>Override</MenuItem>
              <MenuItem value={ROTATION_ROTATE_WITH}>Rotate</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Keep Alive (minutes)"
            type="number"
            inputProps={{ min: 0, step: 1 }}
            value={form.keepAliveMinutes}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, keepAliveMinutes: Number(e.target.value) || 0 }))
            }
            fullWidth
            size="small"
            InputProps={COMPACT_INPUT_PROPS}
            helperText="Minutes"
            InputLabelProps={{ sx: inputLabelSx }}
            sx={textFieldOutlinedSx}
          />

          <FormControl fullWidth size="small">
            <InputLabel sx={inputLabelSx}>Duration</InputLabel>
            <Select
              value={form.durationSeconds}
              label="Duration"
              size="small"
              onChange={(e) =>
                setForm((prev) => ({ ...prev, durationSeconds: Number(e.target.value) }))
              }
              sx={selectOutlinedSx}
              MenuProps={selectMenuProps}
            >
              {DURATION_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}s
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{ color: BLACK, fontSize: '0.8125rem', fontWeight: 600 }}>
            Display During
            {form.rotation === ROTATION_TAKEOVER && (
              <Typography component="span" sx={{ fontWeight: 400, color: 'rgba(0,0,0,0.55)', ml: 0.75, fontSize: '0.75rem' }}>
                (all on for Takeover)
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: -0.25 }}>
            <FormControlLabel
              sx={{
                color: BLACK,
                m: 0,
                '& .MuiFormControlLabel-label': { color: BLACK, fontSize: '0.8125rem' },
              }}
              control={
                <Checkbox
                  size="small"
                  checked={form.duringMusic}
                  disabled={saving || form.rotation === ROTATION_TAKEOVER}
                  onChange={(e) => setForm((prev) => ({ ...prev, duringMusic: e.target.checked }))}
                  sx={{
                    color: BLACK,
                    py: 0,
                    '&.Mui-checked': { color: BLACK },
                    '&.Mui-disabled': { color: 'rgba(0,0,0,0.38)' },
                  }}
                />
              }
              label="Music"
            />
            <FormControlLabel
              sx={{
                color: BLACK,
                m: 0,
                '& .MuiFormControlLabel-label': { color: BLACK, fontSize: '0.8125rem' },
              }}
              control={
                <Checkbox
                  size="small"
                  checked={form.duringAds}
                  disabled={saving || form.rotation === ROTATION_TAKEOVER}
                  onChange={(e) => setForm((prev) => ({ ...prev, duringAds: e.target.checked }))}
                  sx={{
                    color: BLACK,
                    py: 0,
                    '&.Mui-checked': { color: BLACK },
                    '&.Mui-disabled': { color: 'rgba(0,0,0,0.38)' },
                  }}
                />
              }
              label="Ads"
            />
            <FormControlLabel
              sx={{
                color: BLACK,
                m: 0,
                '& .MuiFormControlLabel-label': { color: BLACK, fontSize: '0.8125rem' },
              }}
              control={
                <Checkbox
                  size="small"
                  checked={form.duringOther}
                  disabled={saving || form.rotation === ROTATION_TAKEOVER}
                  onChange={(e) => setForm((prev) => ({ ...prev, duringOther: e.target.checked }))}
                  sx={{
                    color: BLACK,
                    py: 0,
                    '&.Mui-checked': { color: BLACK },
                    '&.Mui-disabled': { color: 'rgba(0,0,0,0.38)' },
                  }}
                />
              }
              label="Other"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 1.5, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            mr: 'auto',
            textTransform: 'none',
            color: '#999999',
            minWidth: 'auto',
            px: 0,
            fontSize: '0.8125rem',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
              color: '#999999',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={() => void handleSubmit(0)}
          disabled={saving}
          disableElevation
          sx={{
            backgroundColor: '#999999',
            color: '#ffffff',
            textTransform: 'none',
            minWidth: 88,
            fontSize: '0.8125rem',
            py: 0.5,
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#888888', boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: '#bdbdbd', color: '#ffffff' },
          }}
        >
          {saveAction === 'save' ? (
            <CircularProgress size={20} sx={{ color: '#ffffff' }} />
          ) : (
            'Save'
          )}
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={() => void handleSubmit(1)}
          disabled={saving}
          disableElevation
          sx={{
            backgroundColor: '#4DAADB',
            color: '#ffffff',
            textTransform: 'none',
            minWidth: 120,
            fontSize: '0.8125rem',
            py: 0.5,
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#409dcf', boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: '#9e9e9e', color: '#ffffff' },
          }}
        >
          {saveAction === 'publish' ? (
            <CircularProgress size={20} sx={{ color: '#ffffff' }} />
          ) : (
            <Box component="span" sx={{ color: '#ffffff' }}>
              Save + Publish
            </Box>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HotKeyFormModal;
